// ---------------------------------------------------------------------------
// HistoryPane — aba "Entregas anteriores" do InputDrawerV3.
// ---------------------------------------------------------------------------
// Conteudo segue DA-001 + A-008:
//   Bloco 1 — Mes corrente: ate 3 ultimos arquivos enviados
//             (quem, quando, nome, tamanho, resultado do parsing)
//             + opcao de "Baixar arquivo bruto" por linha.
//
//   Bloco 2 — Ultimo arquivo enviado para o mes anterior (se houver
//             entrega bem-sucedida): mesma ficha + "Baixar" + "Usar
//             arquivo" (= replicar para o mes selecionado).
//
// Os dados vem do endpoint consolidado ``drawer-details``
// (``current_month_uploads`` e ``previous_month_upload``). Quando o
// backend ainda nao tiver historico (mes virgem), a UI mostra o
// estado vazio com texto explicativo.
// ---------------------------------------------------------------------------

import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import GetApp from "@material-ui/icons/GetApp";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import FileCopy from "@material-ui/icons/FileCopy";

import useDrawerStyles from "../styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import DownloadMenu from "../DownloadMenu";
import DrawerPaneLoadingLayout from "../../DrawerPaneLoadingLayout";
import PaneErrorBanner from "./PaneErrorBanner";

// Centralizado em ``services/inputsV3StateRules`` (PA-006). Mantemos
// o nome local ``parseResultLabel`` por clareza no consumer.
import { inputControlStateLabel as parseResultLabel } from "../../../../../services/inputsV3StateRules";

function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch (_) {
    return String(iso);
  }
}

function initials(name) {
  if (!name) return "—";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

const HistoryPane = ({
  drawerDetails,
  onDownload,
  onReplicate,
  reuseConfigLoaded,
  reuseAllowed,
  isUploading,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "pt";
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();

  const currentUploads = drawerDetails?.currentMonthUploads || [];
  const previousUpload = drawerDetails?.previousMonthUpload || null;

  if (
    !drawerDetails?.error &&
    (Boolean(drawerDetails?.loading) || !drawerDetails?.runtimeInput)
  ) {
    return (
      <DrawerPaneLoadingLayout
        variant="input"
        scrollRef={wheelRef}
        overlayScrollClass={overlayScrollClasses.overlayScroll}
        title={t("modeling_inputs_v3.history_pane.title")}
        message={t("modeling_inputs_v3.history_pane.loading_message")}
      />
    );
  }

  // Build download options using locale labels to support multi-language.
  //
  // Mesmo tratamento do botao de download da timeline (NowPane /
  // ``deriveDownloadOptions``): inputs SENSIVEIS nao guardam o arquivo
  // original (e' purgado apos a anonimizacao), entao a opcao "original"
  // fica VISIVEL porem DESABILITADA (opacidade + sem click) em todas as
  // linhas do historico. ``sensitive`` e' input-level (vale para todos
  // os periodos). O ``DownloadMenu`` respeita ``disabled`` por item.
  const sensitive = !!drawerDetails?.about?.sensitive;
  const originalOption = {
    kind: "original",
    label: t("modeling_inputs_v3.download.original_label"),
    hint: sensitive
      ? t("modeling_inputs_v3.download.original_sensitive_hint")
      : t("modeling_inputs_v3.download.original_hint"),
    disabled: sensitive,
  };
  const DOWNLOAD_OPTIONS_FULL = [
    originalOption,
    {
      kind: "cleaned_up",
      label: t("modeling_inputs_v3.download.cleaned_label"),
      hint: t("modeling_inputs_v3.download.cleaned_hint"),
    },
  ];
  const DOWNLOAD_OPTIONS_ORIGINAL_ONLY = [originalOption];

  const [menu, setMenu] = useState({ anchor: null, item: null });
  const buttonsRef = useRef({});

  const openMenu = (item) =>
    setMenu({ anchor: buttonsRef.current[item._key], item });
  const closeMenu = () => setMenu({ anchor: null, item: null });
  const pick = (kind) => {
    const item = menu.item;
    closeMenu();
    if (onDownload && item) onDownload(kind, item);
  };

  const renderRow = (item, opts = {}) => {
    const { showUseFile = false, isCurrent = false } = opts;
    // Simetria com a toolbar ("Usar mes anterior"): habilitado quando o
    // reuso esta permitido e nada esta em curso. NAO depende de ja
    // existir entrega no mes corrente (``hasDelivered``) — substituir e
    // permitido por decisao de produto (rev. 2026-05-29).
    const useFileDisabled = !reuseConfigLoaded || !reuseAllowed || isUploading;
    const useFileTitle = !reuseConfigLoaded
      ? t("modeling_inputs_v3.history_pane.reuse_loading")
      : !reuseAllowed
        ? t("modeling_inputs_v3.history_pane.reuse_disabled")
        : t("modeling_inputs_v3.history_pane.reuse_btn_title");
    const result = parseResultLabel(item.state, t);
    // O endpoint ``drawer-details`` envia ``by`` (objeto), ``at`` (ISO),
    // ``filename`` e ``size_bytes`` (vide ``_serialize_control_for_history``
    // no backend). Antes desta normalizacao, o pane lia ``who``/
    // ``delivered_at``/``original_filename``/``file_size`` — campos que
    // nao existem na resposta do servidor — e renderizava "— · recebido
    // em —" em qualquer entrega que tivesse acabado de chegar. Normalizar
    // aqui evita ter que duplicar a regra em cada pane consumidor.
    const whoName =
      item.who || (item.by && (item.by.name || item.by.email)) || null;
    const deliveredAt = item.delivered_at || item.at || null;
    const fileName = item.original_filename || item.filename || null;
    const fileSize =
      item.file_size != null
        ? item.file_size
        : item.size_bytes != null
          ? item.size_bytes
          : null;
    const optionsForRow =
      item.state === "error" || item.state === "system_error"
        ? DOWNLOAD_OPTIONS_ORIGINAL_ONLY
        : DOWNLOAD_OPTIONS_FULL;
    const toneCls =
      result.tone === "ok"
        ? classes.historyResultOk
        : result.tone === "warn"
          ? classes.historyResultWarn
          : result.tone === "err"
            ? classes.historyResultErr
            : classes.historyResultInfo;
    return (
      <div key={item._key} className={classes.historyItem}>
        <div className={classes.historyAvatar}>{initials(whoName)}</div>
        <div className={classes.historyBody}>
          <div className={classes.historyTitle}>
            {item.title}
            {isCurrent && (
              <span className={classes.historyCurrentBadge}>
                {t("modeling_inputs_v3.history_pane.badge_current")}
              </span>
            )}
            <span className={`${classes.historyResultBadge} ${toneCls}`}>
              {result.label}
            </span>
          </div>
          <div className={classes.historyMeta}>
            {whoName || "—"} ·{" "}
            {t("modeling_inputs_v3.history_pane.received_at", {
              date: formatDate(deliveredAt),
            })}
            {fileName ? (
              <>
                {" · "}
                <strong>{fileName}</strong>
              </>
            ) : null}
            {fileSize != null && (
              <>
                {" "}
                {" · "} {formatBytes(fileSize)}
              </>
            )}
          </div>
        </div>
        <div className={classes.historyActions}>
          <button
            type="button"
            ref={(el) => {
              buttonsRef.current[item._key] = el;
            }}
            className={`${classes.btn} ${classes.btnGhost} ${classes.btnSmall}`}
            onClick={() => openMenu({ ...item, _options: optionsForRow })}
            disabled={!optionsForRow.length}
          >
            <GetApp />
            {t("modeling_inputs_v3.history_pane.download_btn")}
            <ArrowDropDown />
          </button>
          {showUseFile && onReplicate && (
            <button
              type="button"
              className={`${classes.btn} ${classes.btnGhost} ${classes.btnSmall}`}
              onClick={() => onReplicate(item)}
              disabled={useFileDisabled}
              title={useFileTitle}
            >
              <FileCopy />
              {t("modeling_inputs_v3.history_pane.use_file_btn")}
            </button>
          )}
        </div>
      </div>
    );
  };

  const currentBlock = (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>
        {t("modeling_inputs_v3.history_pane.section_current_month")}
        {currentUploads.length > 0 && (
          <span className={classes.sectionCount}>{currentUploads.length}</span>
        )}
      </div>
      {currentUploads.length === 0 && (
        <div className={classes.historyEmpty}>
          {t("modeling_inputs_v3.history_pane.no_uploads_current")}
        </div>
      )}
      {currentUploads.slice(0, 3).map((u, idx) =>
        renderRow(
          {
            ...u,
            _key: `cur-${idx}`,
            title: t("modeling_inputs_v3.history_pane.this_month_file"),
          },
          { isCurrent: idx === 0 },
        ),
      )}
    </div>
  );

  // Use Intl.DateTimeFormat for month name — resolves to the correct
  // language automatically without a hardcoded month-name array.
  const prevBlockTitle = previousUpload
    ? new Date(previousUpload.year, previousUpload.month - 1).toLocaleString(
        lang,
        { month: "long", year: "numeric" },
      )
    : t("modeling_inputs_v3.history_pane.section_prev_month");

  const previousBlock = (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>
        {t("modeling_inputs_v3.history_pane.section_prev_month")}
        <span className={classes.sectionHint}>{prevBlockTitle}</span>
      </div>
      {!previousUpload && (
        <div className={classes.historyEmpty}>
          {t("modeling_inputs_v3.history_pane.no_prev_upload")}
        </div>
      )}
      {previousUpload &&
        renderRow(
          {
            ...previousUpload,
            _key: "prev",
            title: prevBlockTitle,
          },
          { showUseFile: true },
        )}
    </div>
  );

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      {drawerDetails?.error ? (
        <PaneErrorBanner
          title={t("modeling_inputs_v3.history_pane.error_title")}
          message={t("modeling_inputs_v3.history_pane.error_message")}
          detail={
            drawerDetails.error?.response?.data?.error_code ||
            drawerDetails.error?.message ||
            null
          }
          onRetry={
            typeof drawerDetails.refresh === "function"
              ? drawerDetails.refresh
              : null
          }
          retrying={Boolean(drawerDetails.loading)}
        />
      ) : null}
      {currentBlock}
      {previousBlock}
      <DownloadMenu
        anchorEl={menu.anchor}
        open={Boolean(menu.anchor)}
        onClose={closeMenu}
        options={(menu.item && menu.item._options) || DOWNLOAD_OPTIONS_FULL}
        onPick={pick}
      />
    </div>
  );
};

HistoryPane.propTypes = {
  drawerDetails: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.any,
    runtimeInput: PropTypes.object,
    currentMonthUploads: PropTypes.array,
    previousMonthUpload: PropTypes.object,
    about: PropTypes.shape({
      sensitive: PropTypes.bool,
    }),
    refresh: PropTypes.func,
  }),
  onDownload: PropTypes.func,
  onReplicate: PropTypes.func,
  reuseConfigLoaded: PropTypes.bool,
  reuseAllowed: PropTypes.bool,
  isUploading: PropTypes.bool,
};

HistoryPane.defaultProps = {
  reuseConfigLoaded: false,
  reuseAllowed: false,
  isUploading: false,
};

export default HistoryPane;
